import { UpdatePatientFieldsInput } from '@saas/interfaces/graphql'
import {
    CreatePatientBodyIds,
    AttendingPhysicianIds,
    AttendingPhysicianRppsIds,
    AttendingPhysicianStateIds,
    InsiIdentityStatus2Ids,
    SexIds,
    UpdatePatientBodyIds,
} from '@toktokdoctech/tokapi-client'
import { FormatedPatientFormData } from '../_props'

export type SavablePatientValues = Omit<UpdatePatientFieldsInput, 'hasCss' | 'ltDiseases'> & {
    hasCss?: boolean | null
    hasLtDiseases?: boolean | null
    attendingPhysician: AttendingPhysicianIds
}

export const transformPatientDataBase = (
    savableValues: SavablePatientValues,
    submitValues: FormatedPatientFormData,
    currentIdentityStatus: InsiIdentityStatus2Ids | undefined,
) => ({
    birthName: savableValues.birthName ?? '',
    firstNameList: savableValues.firstNameList ?? [],
    firstFirstName: savableValues.firstFirstName ?? '',
    firstName: savableValues.firstName ?? '',
    lastName: savableValues.lastName ?? '',
    birthDate: savableValues.birthDate ?? '',
    manualIdentityValidation: savableValues.manualIdentityValidation ?? undefined,
    sex: (savableValues.sex as SexIds) ?? '',
    socialSecurityNumber: savableValues.socialSecurityNumber ?? '',
    countryBirth: {
        id: savableValues.countryBirth?.link ?? '',
        name: submitValues.countryBirth?.name ?? '',
    },
    attendingPhysician: computeAttendingPhysicianOrUnknown(savableValues),
    birthCityCode: savableValues.birthCityCode ?? '',
    birthCityLabel: savableValues.birthCityLabel ?? '',
    roomNumber: savableValues.room ?? '',
    hasCss: savableValues.hasCss ?? null,
    hasLtDiseases: savableValues.hasLtDiseases ?? null,
    contact: {
        email: savableValues.contactEmail ?? '',
        phone: savableValues.contactPhone ?? '',
    },
    attachmentOrg: savableValues.attachmentOrg ?? '',
    patientIsSameAsInsured: savableValues.patientIsSameAsInsured ?? false,
    patientInsuredUserLabel: savableValues.patientInsuredUserLabel ?? '',
    insuredSsn: savableValues.insuredSsn ?? '',
    address: {
        street: submitValues.address?.street ?? '',
        city: submitValues.address?.city ?? '',
        postalCode: submitValues.address?.postalCode ?? '',
    },
    identityProof: {
        id: savableValues.identityProof?.link ?? '',
        name: submitValues.identityProof?.label ?? '',
    },
    insiIdentityStatus: {
        id: currentIdentityStatus?.id ?? '',
        name: currentIdentityStatus?.label ?? '',
    },
    insiIdentityAttributes:
        savableValues.insiIdentityAttributes?.map((attr) => ({
            id: attr.objectId ?? '',
            name: attr.label ?? '',
        })) ?? [],
    insNum: savableValues.insNum ?? null,
    insOid: savableValues.insOid ?? null,
})

const computeAttendingPhysicianOrUnknown = ({
    attendingPhysician,
}: SavablePatientValues): AttendingPhysicianRppsIds => {
    const { state, physician } = attendingPhysician

    if (state === AttendingPhysicianStateIds.None) return { state }
    if (state === AttendingPhysicianStateIds.Known && physician) {
        return { state, rpps: physician.rpps }
    }
    return { state: AttendingPhysicianStateIds.Unknown }
}

export const transformCreatePatientData = (
    savableValues: SavablePatientValues,
    submitValues: FormatedPatientFormData,
    currentIdentityStatus: InsiIdentityStatus2Ids | undefined,
): CreatePatientBodyIds => transformPatientDataBase(savableValues, submitValues, currentIdentityStatus)

export const transformUpdatePatientData = (
    savableValues: SavablePatientValues,
    submitValues: FormatedPatientFormData,
    currentIdentityStatus: InsiIdentityStatus2Ids | undefined,
): UpdatePatientBodyIds => transformPatientDataBase(savableValues, submitValues, currentIdentityStatus)
